import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemGetShippingLabelModal } from '@/types/modal';
import { ShipmentDirection, ShippingLabelCarrier } from '@/types/item';
import { getShippingLabelCarriers, getShippingConvenienceLabel, ShippingConvenienceLabel } from '@/services/item';
import { ServiceError } from '@/types/service';
import { StandardModal, ErrorModal } from '@/components/molecules/common/Modal';
import { Dropdown } from '@/components/molecules/common/Form';
import { Skeleton } from '@mui/material';
import { clsx } from 'clsx';
import { useCreateShippingLabel, useGetShippingConvenienceLabel } from '@/hooks/item';
import styles from './Modal.module.scss';
import { DownloadView, renderAndDownload } from '../organisms/item/DownloadLabelView';

const View: React.FC<{ modal: ItemGetShippingLabelModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;
  const [carriers, setCarriers] = useState<ShippingLabelCarrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ServiceError | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<string>('');
  const [selectedLabelType, setSelectedLabelType] = useState<string>('');
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<string>('');
  const [convenienceLabelAvailable, setConvenienceLabelAvailable] = useState<boolean>(false);

  const shipmentDirection = useMemo<ShipmentDirection>(
    () => (item.unlockReturn ? 'return' : 'forward'),
    [item.unlockReturn],
  );
  const createShippingLabelR = useCreateShippingLabel(item.productId);
  const getConvenienceLabelR = useGetShippingConvenienceLabel(item.productId, shipmentDirection);

  const description = useMemo(
    () => t(`item.shipping_status.get_shipping_label.descriptions.${shipmentDirection}`),
    [shipmentDirection, t],
  );

  const downloadCode = (data: ShippingConvenienceLabel) => {
    if (data && item) {
      if (!data.documentUrl) {
        setError({ kind: 'ServerError', message: t('item.shipping_status.get_shipping_label.errors.no_url_found') });
        return;
      }

      renderAndDownload(
        <DownloadView
          shipmentDetails={data}
          product={item}
          shipByLabel={t('MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.SHIP_BY_DATE')}
          trackingLabel={t('MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.TRACKING')}
          refLabel={t('MY_ACCOUNT.ORDERS_SALES.LIST.REF')}
        />,
        `${item.brandName}-${item.productName}-${item.productId}.jpeg`,
        data.documentUrl || '',
      );
    }
  };

  const downloadPdfLabel = async (pdfUrl: string) => {
    if (!item) {
      return;
    }
    try {
      const response = await fetch(pdfUrl, {
        cache: 'no-cache',
      });

      if (!response.ok) {
        setError({ kind: 'ServerError', message: t('item.shipping_status.get_shipping_label.errors.download_failed') });
        return;
      }

      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = url;
      link.download = `${item.brandName}-${item.productName}-${item.productId}.pdf` || 'downloaded-document.pdf'; // Set default filename

      // Append the link to the document body and trigger the click
      document.body.appendChild(link);
      link.click();

      // Clean up: remove the link and revoke the URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError({ kind: 'ServerError', message: t('item.shipping_status.get_shipping_label.errors.download_failed') });
    }
  };

  const triggerAutoDownload = (data: ShippingConvenienceLabel, callback?: () => void) => {
    const { labelType, documentUrl, singleShippingLabelUrl } = data;

    const showError = () =>
      setError({
        kind: 'ServerError',
        message: t('item.shipping_status.get_shipping_label.errors.no_url_found'),
      });

    switch (labelType) {
      case 'printable_label': {
        if (!singleShippingLabelUrl) showError();
        else downloadPdfLabel(singleShippingLabelUrl);
        break;
      }
      case 'shipping_code': {
        if (!documentUrl) showError();
        else downloadCode(data);
        break;
      }
      default:
        break;
    }

    if (callback) callback();
  };

  // Check if convenience label is available on component mount
  useEffect(() => {
    const checkConvenienceLabelAvailability = async () => {
      try {
        const result = await getShippingLabelCarriers(item.productId, shipmentDirection);
        if (result.kind === 'Ok') {
          // If we can get carriers, also check if convenience label is available
          const convenienceResult = await getShippingConvenienceLabel(item.productId, shipmentDirection);
          if (convenienceResult.kind === 'Ok') {
            const { labelType, documentUrl, singleShippingLabelUrl } = convenienceResult.data;
            const downloadUrl = labelType === 'printable_label' ? singleShippingLabelUrl : documentUrl;
            setConvenienceLabelAvailable(!!downloadUrl);
          } else {
            setConvenienceLabelAvailable(false);
          }
        }
      } catch (err) {
        setConvenienceLabelAvailable(false);
      }
    };

    checkConvenienceLabelAvailability();
  }, [item.productId, shipmentDirection]);

  useEffect(() => {
    const fetchCarriers = async () => {
      try {
        setLoading(true);
        const result = await getShippingLabelCarriers(item.productId, shipmentDirection);
        if (result.kind === 'Ok') {
          setCarriers(result.data);
          // Set default selections if available
          const defaultCarrier = result.data.find((carrier: ShippingLabelCarrier) => carrier.selected);
          if (defaultCarrier) {
            setSelectedCarrier(defaultCarrier.name);
            const defaultLabelType = defaultCarrier.shipmentLabelTypes.find(
              (type: { type: string; selected: boolean }) => type.selected,
            );
            if (defaultLabelType) {
              setSelectedLabelType(defaultLabelType.type);
            }
            const defaultShippingMethod = defaultCarrier.dropOffTypes.find(
              (type: { type: string; selected: boolean }) => type.selected,
            );
            if (defaultShippingMethod) {
              setSelectedShippingMethod(defaultShippingMethod.type);
            }
          }
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError({ kind: 'ServerError', message: err instanceof Error ? err.message : 'Unknown error' });
      } finally {
        setLoading(false);
      }
    };

    fetchCarriers();
  }, [item.productId, shipmentDirection]);

  // Handle shipping label creation response
  useEffect(() => {
    if (createShippingLabelR.kind === 'Completed') {
      // After successful shipping label creation, check for convenience label and auto-download
      const checkAndDownloadConvenienceLabel = async () => {
        try {
          const convenienceResult = await getShippingConvenienceLabel(item.productId, shipmentDirection);

          if (convenienceResult.kind === 'Ok') {
            triggerAutoDownload(convenienceResult.data, onClose);
          } else {
            // Show error if convenience label API call fails
            setError({
              kind: 'ServerError',
              message: t('item.shipping_status.get_shipping_label.errors.download_failed'),
            });
          }
        } catch (err) {
          // Show error if convenience label check fails
          setError({
            kind: 'ServerError',
            message: t('item.shipping_status.get_shipping_label.errors.download_failed'),
          });
        }
      };

      checkAndDownloadConvenienceLabel();
    } else if (createShippingLabelR.kind === 'Error') {
      setError(createShippingLabelR.error);
    }
  }, [createShippingLabelR.kind, onClose, item.productId, shipmentDirection]);

  // Handle convenience label download response
  useEffect(() => {
    if (getConvenienceLabelR.kind === 'Completed') {
      triggerAutoDownload(getConvenienceLabelR.data, onClose);
    } else if (getConvenienceLabelR.kind === 'Error') {
      setError(getConvenienceLabelR.error);
    }
  }, [getConvenienceLabelR.kind]);

  const handleSubmit = () => {
    createShippingLabelR.mutate({
      carrierName: selectedCarrier,
      dropOffType: selectedShippingMethod,
      shipmentLabelType: selectedLabelType,
      direction: shipmentDirection,
    });
  };

  const handleDownloadConvenienceLabel = () => {
    getConvenienceLabelR.mutate();
  };

  const selectedCarrierData = carriers.find((carrier) => carrier.name === selectedCarrier);

  // Show loading state for shipping label creation
  if (createShippingLabelR.kind === 'Loading') {
    return (
      <StandardModal
        title={t('item.shipping_status.get_shipping_label.title')}
        onClose={onClose}
        onSubmit={() => {
          // No-op for loading state
        }}
        submittable={false}
        body={
          <>
            <Skeleton
              height={20}
              variant="rounded"
            />
            <div style={{ textAlign: 'center', padding: '20px' }}>
              {t('item.shipping_status.get_shipping_label.generating')}
            </div>
          </>
        }
        modalName="item_get_shipping_label"
      />
    );
  }

  // Show loading state for convenience label download
  if (getConvenienceLabelR.kind === 'Loading') {
    return (
      <StandardModal
        title={t('item.shipping_status.get_shipping_label.title')}
        onClose={onClose}
        onSubmit={() => {
          // No-op for loading state
        }}
        submittable={false}
        body={
          <>
            <Skeleton
              height={20}
              variant="rounded"
            />
            <div style={{ textAlign: 'center', padding: '20px' }}>
              {t('item.shipping_status.get_shipping_label.downloading')}
            </div>
          </>
        }
        modalName="item_get_shipping_label"
      />
    );
  }

  if (loading) {
    return (
      <StandardModal
        title={t('item.shipping_status.get_shipping_label.title')}
        onClose={onClose}
        onSubmit={() => {
          // No-op for loading state
        }}
        submittable={false}
        body={
          <>
            <Skeleton
              height={20}
              variant="rounded"
            />
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
          </>
        }
        modalName="item_get_shipping_label"
      />
    );
  }

  if (error) {
    return (
      <ErrorModal
        title={t('item.shipping_status.get_shipping_label.title')}
        error={error}
        onClose={onClose}
        containerStyle={{
          width: 400,
        }}
      />
    );
  }

  const carrierItems = carriers.map((carrier) => ({
    label: carrier.name,
    value: carrier.name,
  }));

  const labelTypeItems =
    selectedCarrierData?.shipmentLabelTypes.map((type) => ({
      label:
        type.type === 'printable_label'
          ? t('item.shipping_status.get_shipping_label.printable_label')
          : t('item.shipping_status.get_shipping_label.shipping_code'),
      value: type.type,
    })) || [];

  const shippingMethodItems =
    selectedCarrierData?.dropOffTypes.map((type) => ({
      label:
        type.type === 'home_collection'
          ? t('item.shipping_status.get_shipping_label.home_collection')
          : t('item.shipping_status.get_shipping_label.drop_off'),
      value: type.type,
    })) || [];

  const body = (
    <div className={styles.containerGap}>
      <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>

      {convenienceLabelAvailable && (
        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
          <button
            type="button"
            onClick={handleDownloadConvenienceLabel}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            {t('item.shipping_status.get_shipping_label.download_convenience_label')}
          </button>
        </div>
      )}

      <div
        className={clsx([styles.text1rem, styles.textGrey, styles.breakLineWithEnter])}
        dangerouslySetInnerHTML={{ __html: description }}
      />

      <Dropdown
        required={true}
        disabled={false}
        label={t('item.shipping_status.get_shipping_label.carrier')}
        value={selectedCarrier}
        onSelect={setSelectedCarrier}
        items={carrierItems}
      />

      {selectedCarrierData && (
        <>
          <Dropdown
            required={true}
            disabled={false}
            label={t('item.shipping_status.get_shipping_label.label_type')}
            value={selectedLabelType}
            onSelect={setSelectedLabelType}
            items={labelTypeItems}
          />

          <Dropdown
            required={true}
            disabled={false}
            label={t('item.shipping_status.get_shipping_label.shipping_method')}
            value={selectedShippingMethod}
            onSelect={setSelectedShippingMethod}
            items={shippingMethodItems}
          />
        </>
      )}
    </div>
  );

  return (
    <StandardModal
      title={t('item.shipping_status.get_shipping_label.title')}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={t('item.shipping_status.get_shipping_label.generate')}
      submittable={selectedCarrier !== '' && selectedLabelType !== '' && selectedShippingMethod !== ''}
      body={body}
      containerStyle={{ maxWidth: 650 }}
      modalName="item_get_shipping_label"
    />
  );
};

export default View;
